import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, HostBinding, Input } from '@angular/core';

/**
 * This component is a square button that can be customized through inputs for text, colors, and an optional icon.
 * It allows for styling flexibility via binding for background and text color classes, as well as the integration
 * of an icon with alt text.
 *
 * Usage:
 * ```html
 * <app-square-button
 *   [text]="'Click me'"
 *   [action]="onClickHandler"
 *   [backgroundColor]="'bg-primary'"
 *   [textColor]="'text-white'"
 *   [icon]="'path/to/icon.svg'"
 *   [alt]="'icon description'">
 * </app-square-button>
 * ```
 *
 * @selector app-square-button
 */
@Component({
  selector: 'app-square-button',
  imports: [CommonModule],
  templateUrl: './square-button.component.html',
})
export class SquareButtonComponent {
  /** Function to execute when button is clicked */
  @Input() action: ((event: Event) => void) | null | undefined = null;
  /** Text to display on the button */
  @Input() text = '';
  /** Tailwind class for the button's background color */
  @Input() backgroundColor = 'bg-secondary-blue';
  /** Tailwind class for the button's text color */
  @Input() textColor = 'text-white';
  /** Path to an optional icon to display on the button */
  @Input() icon = '';
  /** Alt text for the optional icon */
  @Input() alt = '';
  /** Host binding to set Tailwind class on the host element of the component */
  @HostBinding('class') hostClasses = 'flex w-full';

  /** Generates dynamic class bindings based on inputs */
  getDynamicClasses() {
    return {
      [this.textColor]: !!this.textColor,
      [this.backgroundColor]: !!this.backgroundColor,
    };
  }
}
